import React from 'react';
import { Controller } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import useCreateCategory from './createCategory-hooks';
import { useDirectoriesHook, useGeneralHooks } from '../../../../../../Hooks/hooks';
import { ICreateCategoryForm } from '../../../../../../Interfaces/formValue-interfaces';
import { ButtonTypes, CreateFormTypes } from '../../../../../../Interfaces/componentTypes';
import useLanguagesHooks from '../../../../../../Components/Languages/setLanguage-hooks';
import FileUploader from '../../../../../../Components/FileUploader/fileUploader';
import AuthInput from '../../../../../../Components/Inputs/AuthInput/authInput';
import Button from '../../../../../../Components/Button/button';
import AutoComplete from '../../../../../../Components/Inputs/AutoComplete/autoComplete';
import { UploadImage } from '../../../../../../Assets/Images';
import styles from './createCategory.module.scss';

export interface ICreateCategoryProps {
    company_id: number,
    onClose: () => void,
    cb:any,
};

const CreateCategoryDialog: React.FC<ICreateCategoryProps> = (props) => {
    const { activeLangSrc, t, activeLanguage, checkableLanguages } = useLanguagesHooks();
    const { baseURL } = useGeneralHooks();
    const { menuTypeOptions } = useDirectoriesHook();
    const {
        errors,
        control,
        imageUrl,
        activeForm,
        dialogRef,
        imageLoading,
        createLoading,
        isFormChanged,
        createValidation,
        categoryResponce,
        updateCreateLoading,
        onCreate,
        onUpdate,
        register,
        onCloseDialog,
        handleFileInputChange,
    } = useCreateCategory(props);

    return (
        <div className={styles.container} ref={dialogRef} >
            <div className={styles.header}>
                <p>
                    {activeForm === CreateFormTypes.CREATE
                        ?
                        t('My_Business.Create_Category.Header')
                        :
                        t('My_Business.Create_Category.Header_2')
                    }
                </p>
            </div>
            <form className={styles.form}>
                {
                    activeForm === CreateFormTypes.CREATE
                        ?
                        <>
                            <div className={styles.item}>
                                <div className={styles.uploadContainer}>
                                    <div className={styles.label}>
                                        <p>{t('My_Business.Create_Category.Image')}</p>
                                    </div>
                                    <div className={styles.imageBox}>
                                        {
                                            imageLoading
                                                ?
                                                <ClipLoader color="#aa002b" size={40} />
                                                :
                                                <img
                                                    src={imageUrl ?? UploadImage}
                                                    alt={`${imageUrl}.png`}
                                                    className={styles.image}
                                                />
                                        }
                                    </div>
                                    <FileUploader
                                        id='logo'
                                        handleFileInputChange={handleFileInputChange}
                                        buttonTitleStyle={styles.buttonTitle}
                                        buttonStyle={`${styles.uploadButton}`}
                                    />
                                </div>
                            </div>
                            <div className={styles.item}>
                                <div className={styles.label}>
                                    <img src={activeLangSrc} alt={`${activeLangSrc}.png`} />
                                    <p>
                                        {t('My_Business.Create_Category.Name')}
                                    </p>
                                </div>
                                <AuthInput
                                    register={register}
                                    registerName='name'
                                    labelStyle={styles.inputLabel}
                                    error={errors.name}
                                    patternValue={/.*[A-Za-zÀ-ÖØ-öø-ÿԱ-Ֆա-ֆА-Яа-яҐґЄєІіЇїΆ-ώΑ-ωא-ת一-龯ぁ-ゔァ-ヾㄱ-ㅎㅏ-ㅣ가-힣].*/}
                                />
                            </div>
                            <div className={styles.item}>
                                <div className={styles.label}>
                                    <p>
                                        {t('My_Business.Create_Category.Type')}
                                    </p>
                                </div>
                                <Controller
                                    control={control}
                                    name='menu_type'
                                    rules={{
                                        required: t('Errors.Required'),
                                    }}
                                    render={({ field: { onChange, name, value } }) => {
                                        return (
                                            <AutoComplete
                                                value={value}
                                                name={name}
                                                onChange={onChange}
                                                id='user_id'
                                                data={menuTypeOptions}
                                                error={errors.menu_type}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className={styles.buttonsRow}>
                                <Button
                                    buttonType={ButtonTypes.Secondary}
                                    title={t('Buttons.Close')}
                                    type='button'
                                    onClick={onCloseDialog}
                                    buttonStyle={styles.closeButton}
                                />
                                <Button
                                    buttonType={createValidation ? ButtonTypes.Primery : ButtonTypes.Disabled}
                                    title={t('Buttons.Create')}
                                    type='button'
                                    onClick={onCreate}
                                    isLoading={createLoading}
                                    buttonStyle={styles.button}
                                    disable={!createValidation}
                                />
                            </div>
                        </>
                        :
                        <>
                            {categoryResponce ?
                                <>
                                    <img
                                        src={imageUrl ?? `${baseURL}${categoryResponce.default_image}`}
                                        alt={`${imageUrl}.png`}
                                        className={styles.uploadedImage}
                                    />
                                    <div className={styles.item}>
                                        <div className={styles.label}>
                                            <img src={activeLangSrc} alt={`${activeLangSrc}.png`} />
                                            <p>
                                                {t('My_Business.Create_Category.Title')}
                                            </p>
                                        </div>
                                        <AuthInput
                                            register={register}
                                            registerName={`name_${activeLanguage}`}
                                            labelStyle={styles.inputLabel}
                                            error={errors[`name_${activeLanguage}` as keyof ICreateCategoryForm]}
                                        />
                                    </div>
                                    {
                                        checkableLanguages.map((item) => {
                                            return (
                                                <div className={styles.item} key={item.id}>
                                                    <div className={styles.label}>
                                                        <img src={item.icon} alt={`${item.icon}.png`} />
                                                        <p>
                                                            {t('My_Business.Create_Category.Title')}
                                                        </p>
                                                    </div>
                                                    <AuthInput
                                                        register={register}
                                                        registerName={`name_${item.code}`}
                                                        labelStyle={styles.inputLabel}
                                                        error={errors[`name_${item.code}` as keyof ICreateCategoryForm]}
                                                    />
                                                </div>
                                            )
                                        })
                                    }
                                    <div className={styles.buttonsRow}>
                                        <Button
                                            buttonType={ButtonTypes.Secondary}
                                            title={t('Buttons.Close')}
                                            type='button'
                                            onClick={onCloseDialog}
                                            buttonStyle={styles.closeButton}
                                        />
                                        <Button
                                            buttonType={isFormChanged && createValidation ? ButtonTypes.Primery : ButtonTypes.Disabled}
                                            title={t('Buttons.Save')}
                                            type='button'
                                            onClick={onUpdate}
                                            isLoading={updateCreateLoading}
                                            buttonStyle={styles.button}
                                            disable={!isFormChanged}
                                        />
                                    </div>
                                </>
                                :
                                <ClipLoader color="#aa002b" size={40} />
                            }
                        </>
                }
            </form>
        </div>
    )
};

export default CreateCategoryDialog
